import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Layers, CalendarDays, Trash2, X, Loader2 } from 'lucide-react';
import { useToast } from './Toast';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const LEVELS = [{ key: 'nybegynner', label: 'Nybegynner' }, { key: 'bronse', label: 'Bronse' }];

const Modal = ({ title, onClose, children, testid }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return createPortal(
  <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[120] p-4" onClick={onClose} data-testid={`${testid}-overlay`}>
    <div className="bg-gray-800 rounded-lg p-5 max-w-md w-full space-y-4" onClick={(e) => e.stopPropagation()} data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-white font-semibold">{title}</h3>
        <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-white" aria-label="Lukk" data-testid={`${testid}-close`}><X className="w-5 h-5" /></button>
      </div>
      {children}
    </div>
  </div>, document.body);
};

const StartLevelForm = ({ combos, level, setLevel }) => (
  <>
    <p className="text-sm text-gray-300">
      Gjelder <strong>{combos}</strong> medlem/øvelse-kombinasjon{combos === 1 ? '' : 'er'}. Alle serier i øvelsen vurderes på nytt fra valgt nivå,
      og Aktivlogg husker startnivået for fremtidige registreringer.
    </p>
    <div className="grid grid-cols-2 gap-2">
      {LEVELS.map(l => (
        <button key={l.key} type="button" onClick={() => setLevel(l.key)} data-testid={`bulk-level-${l.key}`}
          className={`h-11 rounded-lg border text-sm font-medium ${level === l.key ? 'bg-yellow-500 text-gray-900 border-yellow-400' : 'bg-gray-700 text-gray-200 border-gray-600'}`}>
          {l.label}
        </button>
      ))}
    </div>
    <p className="text-xs text-amber-300/90">Merker som ikke lenger er oppnådd blir gjenåpnet. Endringen kan angres i endringsloggen.</p>
  </>
);

const DateProgramForm = ({ date, setDate, program, setProgram }) => (
  <>
    <p className="text-sm text-gray-300">Fyll inn det som skal endres. Tomme felt beholdes som før.</p>
    <label className="block text-xs text-gray-400">Ny dato
      <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="bulk-date-input"
        className="mt-1 w-full bg-gray-700 border border-gray-600 rounded-lg px-3 h-10 text-white text-sm" />
    </label>
    <label className="block text-xs text-gray-400">Nytt program
      <input type="text" value={program} onChange={(e) => setProgram(e.target.value)} placeholder="Uendret" data-testid="bulk-program-input"
        className="mt-1 w-full bg-gray-700 border border-gray-600 rounded-lg px-3 h-10 text-white text-sm" />
    </label>
  </>
);

export const MerkeBulkBar = ({ selected, onClear, onDone }) => {
  const toast = useToast();
  const [mode, setMode] = useState(null);
  const [busy, setBusy] = useState(false);
  const [level, setLevel] = useState('bronse');
  const [date, setDate] = useState('');
  const [program, setProgram] = useState('');
  const ids = selected.map(r => r.id);
  const combos = new Set(selected.map(r => `${r.member_id}|${r.discipline_key}|${r.variant_key}`)).size;

  const run = async () => {
    const body = { action: mode, ids };
    if (mode === 'start_level') body.start_level = level;
    if (mode === 'update') {
      if (!date && !program.trim()) { toast.error('Fyll inn dato eller program'); return; }
      if (date) body.result_date = date;
      if (program.trim()) body.program = program.trim();
    }
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(`${API_BASE}/api/merkeskyting/results/bulk`, body, { headers: { Authorization: `Bearer ${token}` } });
      const msg = { delete: `${r.data.count} serier slettet`, update: `${r.data.count} serier oppdatert`, start_level: `Startnivå endret for ${r.data.count} medlem/øvelse` }[mode];
      toast.success(msg);
      setMode(null); setDate(''); setProgram('');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke utføre endringen');
    } finally { setBusy(false); }
  };

  const titles = { start_level: 'Endre startnivå', update: 'Endre dato / program', delete: `Slette ${ids.length} serier?` };
  const btn = 'inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm font-medium touch-manipulation';

  return (
    <div className="bg-gray-900/95 backdrop-blur border border-yellow-500/40 rounded-lg p-2.5 flex flex-wrap items-center gap-2" data-testid="results-bulk-bar">
      <span className="text-sm text-yellow-300 font-semibold mr-1" data-testid="results-bulk-count">{ids.length} valgt</span>
      <button type="button" onClick={() => setMode('start_level')} className={`${btn} bg-yellow-500 text-gray-900 hover:bg-yellow-400`} data-testid="bulk-start-level-btn"><Layers className="w-4 h-4" />Startnivå</button>
      <button type="button" onClick={() => setMode('update')} className={`${btn} bg-gray-700 text-gray-100 hover:bg-gray-600`} data-testid="bulk-update-btn"><CalendarDays className="w-4 h-4" />Dato / program</button>
      <button type="button" onClick={() => setMode('delete')} className={`${btn} bg-red-600/80 text-white hover:bg-red-600`} data-testid="bulk-delete-btn"><Trash2 className="w-4 h-4" />Slett</button>
      <button type="button" onClick={onClear} className={`${btn} text-gray-300 hover:text-white ml-auto`} data-testid="bulk-clear-btn"><X className="w-4 h-4" />Fjern valg</button>

      {mode && (
        <Modal title={titles[mode]} onClose={() => !busy && setMode(null)} testid="bulk-modal">
          {mode === 'start_level' && <StartLevelForm combos={combos} level={level} setLevel={setLevel} />}
          {mode === 'update' && <DateProgramForm date={date} setDate={setDate} program={program} setProgram={setProgram} />}
          {mode === 'delete' && <p className="text-sm text-gray-300">Progresjon og merker reberegnes. Slettingen kan angres i endringsloggen.</p>}
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setMode(null)} disabled={busy} className="h-10 px-4 rounded-lg bg-gray-700 text-gray-200 text-sm" data-testid="bulk-cancel-btn">Avbryt</button>
            <button type="button" onClick={run} disabled={busy} data-testid="bulk-confirm-btn"
              className={`h-10 px-4 rounded-lg text-sm font-semibold inline-flex items-center gap-2 ${mode === 'delete' ? 'bg-red-600 text-white' : 'bg-yellow-500 text-gray-900'}`}>
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}{mode === 'delete' ? 'Slett' : 'Lagre'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default MerkeBulkBar;
